import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
} from "@mui/material";
import { PredictiveAddressSearch } from "components/organisations/pav-address-input";
import { IndustryAutoComplete } from "components/shared/industry-auto-complete";
import { ViewEditInput } from "components/shared/view-edit-input";
import { industries } from "components/user/sign-in/states";
import { GetUserIfLoggedIn, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Organisation } from "data/models";
import { editOrganisation } from "data/mutations";
import { fetchOrganisation } from "data/queries";
import { Form, Formik } from "formik";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { resolveIdentityId } from "utils/identity";
import { object, string } from "yup";

const orgCreationValidator = object({
  name: string().required("Organisation name is required"),
  email: string().email("Enter a valid email").required("Email is required"),
  abn: string()
    .matches(/^\d+$/, "ABN must be 11 digits")
    .min(11, "ABN must be 11 digits")
    .max(11, "ABN must be 11 digits")
    .nullable(true),
  address: string().nullable(true),
  phoneNumber: string()
    .matches(
      /^\d+$/,
      "Please provide a valid Australian phone number without the country code"
    )
    .min(
      10,
      "Please provide a valid Australian phone number without the country code"
    )
    .max(
      10,
      "Please provide a valid Australian phone number without the country code"
    )
    .nullable(true),
  industry: string()
    .oneOf([null, ...industries])
    .nullable(true),
  id: string().nullable(true),
});

export const GeneralSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);

  const [identity, setIdentity] = useIdentity();
  const identityId = resolveIdentityId(identity);

  const queryClient = useQueryClient();
  const organisationQuery = useQuery([QueryKeys.organisation, identityId], () =>
    fetchOrganisation()
  );
  const organisationMutation = useMutation(
    (organisation: Organisation) => editOrganisation(organisation),
    {
      onSuccess: (updatedOrganisation) => {
        queryClient.setQueryData(
          [QueryKeys.organisation, identityId],
          updatedOrganisation
        );

        setIdentity((prevIdentity) => {
          const user = GetUserIfLoggedIn(prevIdentity);
          if (user) {
            return {
              ...user,
              organisations: user.organisations.map((org) => {
                if (org.active) {
                  return {
                    ...org,
                    name: updatedOrganisation.name,
                  };
                } else {
                  return org;
                }
              }),
            };
          } else {
            return prevIdentity;
          }
        });

        setIsEditing(false);
        setIsEditingContact(false);
      },
    }
  );

  return (
    <>
      {organisationQuery.isError && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CardHeader title="Organisation Profile" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Alert variant="outlined" severity="error">
                  An error occurred while fetching the organisation details.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardHeader title="Contact Details" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Alert variant="outlined" severity="error">
                  An error occurred while fetching the organisation details.
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {organisationQuery.isLoading && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CardHeader title="Organisation Profile" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Skeleton variant="rectangular" height="200px" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <CardHeader title="Contact Details" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Skeleton variant="rectangular" height="200px" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {organisationQuery.isSuccess && (
        <Formik<Organisation>
          enableReinitialize={true}
          initialValues={{
            name: organisationQuery.data.name,
            id: organisationQuery.data.id,
            email: organisationQuery.data.email,
            address: organisationQuery.data.address,
            industry: organisationQuery.data.industry,
            phoneNumber: organisationQuery.data.phoneNumber,
            abn: organisationQuery.data.abn,
            orgSize: organisationQuery.data.orgSize,
          }}
          onSubmit={async (values) => {
            organisationMutation.mutate({
              id: organisationQuery.data?.id,
              ...values,
            });
          }}
          validationSchema={orgCreationValidator}
        >
          {({
            values,
            handleChange,
            isSubmitting,
            setFieldValue,
            handleBlur,
            errors,
            touched,
            resetForm,
            isValid,
            dirty,
          }: any) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CardHeader title="Organisation Profile" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box
                        gap={1}
                        display="flex"
                        flexDirection="column"
                        marginBottom={isEditing ? 0 : 1}
                      >
                        <ViewEditInput isEditing={isEditing}>
                          <TextField
                            id="name"
                            name="name"
                            label="Organisation Name"
                            type="text"
                            margin="normal"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.name && Boolean(errors.name)}
                            value={values.name}
                            variant="outlined"
                            fullWidth
                            size="small"
                            helperText={touched.name && errors.name}
                            inputProps={{ maxLength: 64 }}
                            sx={{ padding: "0px", margin: "0px", marginTop: 1 }}
                          />
                        </ViewEditInput>
                        <ViewEditInput isEditing={isEditing}>
                          <TextField
                            id="id"
                            size="small"
                            label="Organisation Id"
                            name="id"
                            value={values.id}
                            error={touched.id && Boolean(errors.id)}
                            helperText={touched.id && errors.id}
                            disabled
                          />
                        </ViewEditInput>
                        <ViewEditInput isEditing={isEditing}>
                          <IndustryAutoComplete
                            size="small"
                            label="Industry"
                            value={values.industry}
                            onChange={handleChange}
                            setFieldValue={setFieldValue}
                            readOnly={
                              !isEditing ||
                              organisationQuery.isLoading ||
                              isSubmitting
                            }
                            disabled={
                              !isEditing ||
                              organisationQuery.isLoading ||
                              isSubmitting
                            }
                            error={
                              touched.industry !== null &&
                              touched.industry &&
                              Boolean(errors.industry)
                            }
                            helperText={touched.industry && errors.industry}
                          />
                        </ViewEditInput>
                        <ViewEditInput
                          isEditing={isEditing}
                          label="Organisation Size"
                          value={values.orgSize}
                        >
                          <FormControl fullWidth>
                            <InputLabel id="org-size-select-label">
                              Organisation Size
                            </InputLabel>
                            <Select
                              labelId="org-size-select-label"
                              id="orgSize"
                              name="orgSize"
                              value={values.orgSize}
                              label="Organisation Size"
                              onChange={handleChange}
                              size="small"
                            >
                              <MenuItem value="1-10 employees">
                                1-10 employees (Micro business/Startup)
                              </MenuItem>
                              <MenuItem value="11-50 employees">
                                11-50 employees (Small business)
                              </MenuItem>
                              <MenuItem value="51-200 employees">
                                51-200 employees (Mid-sized business)
                              </MenuItem>
                              <MenuItem value="201-500 employees">
                                201-500 employees (Large mid-market)
                              </MenuItem>
                              <MenuItem value="501-1,000 employees">
                                501-1,000 employees (Small enterprise)
                              </MenuItem>
                              <MenuItem value="1,001-5,000 employees">
                                1,001-5,000 employees (Mid enterprise)
                              </MenuItem>
                              <MenuItem value="5,001-10,000 employees">
                                5,001-10,000 employees (Large enterprise)
                              </MenuItem>
                              <MenuItem value="10,000+ employees">
                                10,000+ employees (Global enterprise)
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </ViewEditInput>
                        <ViewEditInput isEditing={isEditing}>
                          <TextField
                            id="abn"
                            size="small"
                            label="ABN"
                            name="abn"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.abn}
                            inputProps={{ maxLength: 11 }}
                            error={touched.abn && Boolean(errors.abn)}
                            helperText={touched.abn && errors.abn}
                          />
                        </ViewEditInput>
                      </Box>
                    </CardContent>
                    <CardActions
                      sx={{
                        justifyContent: "flex-end",
                      }}
                    >
                      {!isEditing && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setIsEditing(true)}
                        >
                          Edit
                        </Button>
                      )}
                      {isEditing && (
                        <Box display="flex" flexDirection="row" gap={2}>
                          <Button
                            size="small"
                            onClick={() => {
                              resetForm();
                              setIsEditing(false);
                            }}
                            disabled={organisationMutation.isLoading}
                          >
                            Cancel
                          </Button>
                          <LoadingButton
                            loading={organisationMutation.isLoading}
                            size="small"
                            variant="contained"
                            type="submit"
                            disabled={!isValid || !dirty}
                          >
                            Save
                          </LoadingButton>
                        </Box>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CardHeader title="Contact Details" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box
                        gap={1}
                        display="flex"
                        flexDirection="column"
                        marginBottom={isEditingContact ? 0 : 1}
                      >
                        <ViewEditInput isEditing={isEditingContact}>
                          <TextField
                            id="email"
                            size="small"
                            label="Email"
                            name="email"
                            type="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            sx={{ padding: "0px", margin: "0px", marginTop: 1 }}
                          />
                        </ViewEditInput>
                        <ViewEditInput isEditing={isEditingContact}>
                          <TextField
                            id="phoneNumber"
                            size="small"
                            label="Phone"
                            name="phoneNumber"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phoneNumber}
                            error={
                              touched.phoneNumber && Boolean(errors.phoneNumber)
                            }
                            helperText={
                              touched.phoneNumber && errors.phoneNumber
                            }
                          />
                        </ViewEditInput>
                        <ViewEditInput isEditing={isEditingContact}>
                          <PredictiveAddressSearch
                            disabled={
                              organisationQuery.isLoading || !isEditingContact
                            }
                            readOnly={
                              organisationQuery.isLoading || !isEditingContact
                            }
                            textFieldStyle={{
                              marginTop: "0px",
                              marginBottom: "0px",
                            }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            setFieldValue={setFieldValue}
                            id="address"
                            size="small"
                            name="address"
                            label="Address"
                            value={values.address}
                            error={
                              touched.address !== null &&
                              touched.address &&
                              Boolean(errors.address)
                            }
                            helperText={touched.address && errors.address}
                          />
                        </ViewEditInput>
                      </Box>
                    </CardContent>
                    <CardActions
                      sx={{
                        justifyContent: "flex-end",
                      }}
                    >
                      {!isEditingContact && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setIsEditingContact(true)}
                        >
                          Edit
                        </Button>
                      )}
                      {isEditingContact && (
                        <Box display="flex" flexDirection="row" gap={2}>
                          <Button
                            size="small"
                            onClick={() => {
                              resetForm();
                              setIsEditingContact(false);
                            }}
                            disabled={organisationMutation.isLoading}
                          >
                            Cancel
                          </Button>
                          <LoadingButton
                            loading={organisationMutation.isLoading}
                            size="small"
                            variant="contained"
                            type="submit"
                            disabled={!isValid || !dirty}
                          >
                            Save
                          </LoadingButton>
                        </Box>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
